import { useEffect, useState } from 'react'
import * as Yup from 'yup'
import { Link, useNavigate } from 'react-router-dom'
import { Field, FormikProvider, useFormik } from 'formik'
import { useDispatch, useSelector } from 'react-redux'
import { checkSupplierPassword, login } from '../../../redux/features/auth/authActions'
import { MESSAGES } from '../../../utlis/messages'
import FieldInputText from '../../common/FieldInputText'
import SubmitButton from '../../common/SubmitButton'
import { toast } from "react-toastify"

const supplierCodeSchema = Yup.object({
  email: Yup.string().email(MESSAGES.INVALID_EMAIL_MESSAGE).required(MESSAGES.EMAIL_REQUIRED),
});

const passwordSchema = Yup.object({
  password: Yup.string().min(8, MESSAGES.PASSWORD_LENGTH_MESSAGE).required(MESSAGES.PASSWORD_REQUIRED),
});

const initialValues = {
  email: '',
  password: '',
}

export function Login() {
  const dispatch: any = useDispatch();
  const navigate = useNavigate();
  const authState = useSelector((state: any) => state.auth);
  const [step, setStep] = useState<'supplier' | 'password'>('supplier');
  const [supplierValid, setSupplierValid] = useState(false);
  const [supplierChecked, setSupplierChecked] = useState(false);

  useEffect(() => {
    if (authState.token) {
      navigate('/dashboard');
    }
  }, [authState.token]);

  const formik = useFormik({
    initialValues,
    validationSchema: step === 'supplier' ? supplierCodeSchema : passwordSchema,
    onSubmit: async (values, { setSubmitting }) => {
      
      if (step === 'supplier') {
        const { email } = values;
        const result = await dispatch(checkSupplierPassword(email));

        if (checkSupplierPassword.fulfilled.match(result)) {
          const hasPassword = result.payload.emailExists;
          setSupplierChecked(true);

          if (hasPassword) {
            setSupplierValid(true);
            setStep('password');
          } else {
            setSupplierValid(false);
            toast.error(MESSAGES.PASSWORD_NOT_LINKED);
          }
        } else {
          toast.error(result?.payload?.responseMessage || MESSAGES.SUPPLIER_CHECK_FAILED);
        }

        setSubmitting(false);
        return;
      }

      // Login step
      try {
        const loginResult = await dispatch(login(values));

        if (login.fulfilled.match(loginResult)) {
          navigate('/dashboard');
        }
      } catch (error) {
        toast.error(MESSAGES.LOGIN_ERROR);
      }
    }
  });

  return (
    <FormikProvider value={formik}>
      <form className='form w-100 authWrapper' onSubmit={formik.handleSubmit} noValidate>
        <div className='text-center mb-11'>
          <h1 className='text-dark fw-bolder mb-3'>{MESSAGES.LOADING_SUPPLIER_INVOICE}</h1>
        </div>

        <div className='fv-row mb-8'>
          <Field
            name="email"
            label={MESSAGES.EMAIL}
            type="email"
            required
            disabled={step === 'password'}
            component={FieldInputText}
            placeholder={MESSAGES.EMAIL}
          />

          {/* Show registration link only when supplierChecked and not valid */}
          {!supplierValid && step === 'supplier' && supplierChecked && (
            <div className='text-center'>
              <span className='text-gray-600 fw-semibold'>
                {MESSAGES.EMAIL_PASSWORD_NOT_SAVED}: {' '}
                <Link
                  to='/auth/registration'
                  state={{ email: formik.values.email }}
                  className='link-primary'
                >
                  {MESSAGES.PLEASE_SAVE_HERE}
                </Link>
              </span>
            </div>
          )}
        </div>

        {/* Show password only if valid supplier */}
        {supplierValid && step === 'password' && (
          <>
            <div className='fv-row mb-3'>
              <Field
                name="password"
                label="Password"
                type="password"
                required
                component={FieldInputText}
                placeholder="Password"
              />
            </div>
            <div className='d-flex flex-stack flex-wrap gap-3 fs-base fw-semibold mb-8'>
              <div />
              <Link to='/auth/forgot-password' className='link-primary'>
                {MESSAGES.FORGOT_YOUR_PASSWORD}
              </Link>
            </div>
          </>
        )}

        <div className='d-grid mb-10'>
          <SubmitButton
            title={step === 'supplier' ? 'Next' : 'Login'}
            type="submit"
            loading={authState.loading || formik.isSubmitting}
          />
        </div>
      </form>
    </FormikProvider>
  );
}
